import { Spacing } from '@persona-id/design-tokens';
import {
  CircleHelpIcon,
  CircleInfoIcon,
  PlusIcon,
  TransferOutIcon,
} from '@persona-id/design-tokens/icons';
import Fuse from 'fuse.js';
import produce from 'immer';
import { uniq } from 'lodash';
import { snakeCase } from 'lodash/fp';
import { Fragment, memo, ReactNode, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import { FieldArray, FormSection, WrappedFieldArrayProps } from 'redux-form';
import styled from 'styled-components';

import AgeComparisonConfigurationElement from 'dashboard/apps/InquiryTemplateEditor/AgeComparisonConfigurationElement';
import { stubDefaultCheck } from 'dashboard/apps/InquiryTemplateEditor/forms/utils';
import useHasAccessForTierDeterminator from 'dashboard/apps/InquiryTemplateEditor/hooks/useHasAccessForTierDeterminator';
import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import useIsITEInternal from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsITEInternal';
import InquiriesGatingSticker, {
  EnhancedGatingStickerType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiriesGatingSticker';
import {
  FullSpanSubRowTD,
  SectionDescription,
  TableSubRow,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import { getVtmplvDisplayName } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/utils';
import Accordion, { TitleContainer as CheckTitleContainer } from 'dashboard/components/Accordion';
import BiometricBadge from 'dashboard/components/BiometricBadge';
import { BetaLabel, SunsetLabel } from 'dashboard/components/ChecksTableRow/Components';
import ContextMenu from 'dashboard/components/ContextMenu';
import ProductUpsellTooltip from 'dashboard/components/ProductUpsellTooltip';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import SearchInput from 'dashboard/components-lib/SearchInput';
import BarcodeCheckModal, {
  getBarcodeIdsWithMissingCollection,
  hasBarcodeIdsEnabled,
  isBarcodeDependentCheck,
  isGovIdVtmplVersion,
} from 'dashboard/forms/FormVerificationTemplateChecks/BarcodeCheckModal';
import BarcodeDetectionConfigurationElement from 'dashboard/forms/FormVerificationTemplateChecks/BarcodeDetectionConfigurationElement';
import CheckWarningModal, {
  isExplicitWarningCheck,
} from 'dashboard/forms/FormVerificationTemplateChecks/CheckWarningModal';
import { DOCUMENT_TYPE_TO_RECOMMENDED_CONFIG } from 'dashboard/forms/FormVerificationTemplateChecks/constants';
import DisallowedCountryConfigurationElement from 'dashboard/forms/FormVerificationTemplateChecks/DisallowedCountryConfigurationElement';
import DocumentExtractedPropertiesConfigurationElement from 'dashboard/forms/FormVerificationTemplateChecks/DocumentExtractedPropertiesConfigurationElement';
import { DocumentRecencyConfigurationElement } from 'dashboard/forms/FormVerificationTemplateChecks/DocumentRecencyConfigurationElement';
import DocumentTypeConfigurationElement from 'dashboard/forms/FormVerificationTemplateChecks/DocumentTypeConfigurationElement';
import ExtractedPropertiesConfigurationElement from 'dashboard/forms/FormVerificationTemplateChecks/ExtractedPropertiesConfigurationElement';
import MatchRequirementsConfigurationElement from 'dashboard/forms/FormVerificationTemplateChecks/MatchRequirementsConfigurationElement';
import MrzDetectionConfigurationElement from 'dashboard/forms/FormVerificationTemplateChecks/MrzDetectionConfigurationElement';
import RiskConfigConfigurationElement from 'dashboard/forms/FormVerificationTemplateChecks/RiskConfigConfigurationElement';
import SelfieAgeThresholdComparisonConfigurationElement from 'dashboard/forms/FormVerificationTemplateChecks/SelfieAgeThresholdComparisonConfigurationElement';
import SelfieLivenessConfigurationElement from 'dashboard/forms/FormVerificationTemplateChecks/SelfieLivenessConfigurationElement';
import TableLayout, {
  TableIconTD,
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import {
  MatchRequirementKeys,
  MatchRequirementProfile,
} from 'dashboard/models/match-requirement-profile';
import { ProductFeatureName } from 'dashboard/models/organization';
import {
  checkEnabledByTier,
  isAlphaCheck,
  isBetaCheck,
  isDeprecatedCheck,
  isIdCompareCheck,
  isLegacyInternationalDatabaseCheck,
  isMatchRequirementsCheck,
  isRiskConfigCheck,
  isSunsetCheck,
  ITE_OMITTED_CHECKS,
} from 'dashboard/models/verification-check';
import { isDisallowedCountryCheck } from 'dashboard/models/verification-check-config/disallowed-country-detection';
import { isDocumentExtractionCheck } from 'dashboard/models/verification-check-config/document-extracted-properties-detection';
import { isDocumentRecencyCheck } from 'dashboard/models/verification-check-config/document-recency-detection';
import {
  DocumentType,
  isDocumentTypeCheck,
} from 'dashboard/models/verification-check-config/document-type-detection';
import { isAgeLimitCheck } from 'dashboard/models/verification-check-config/id-age-comparison';
import { isBarcodeDetectionCheck } from 'dashboard/models/verification-check-config/id-barcode-detection';
import { isExpirationCheck } from 'dashboard/models/verification-check-config/id-expiration-detection';
import { isExtractionCheck } from 'dashboard/models/verification-check-config/id-extracted-properties-detection';
import { isMrzDetectionCheck } from 'dashboard/models/verification-check-config/id-mrz-detection';
import { isSelfieAgeThresholdCheck } from 'dashboard/models/verification-check-config/selfie-age-threshold-comparison';
import { isSelfieLivenessCheck } from 'dashboard/models/verification-check-config/selfie-liveness-detection';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import {
  GovernmentIdEnabledIdClassByCountries,
  VerificationTemplateVersion,
  VerificationTemplateVersionDocument,
} from 'dashboard/models/verification-template-version';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { paragraphMixins } from 'design-system/mixins/text';
import { CHECK_DISPLAY_CONFIG } from 'lib/constants/check';
import useDebounced from 'lib/hooks/useDebounced';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useProductFeature from 'lib/hooks/useProductFeature';
import { ApiModel } from 'lib/interfaces/api';
import { VerificationStatus } from 'lib/verification';
import {
  CheckName,
  getChecks,
  needsBiometricsProcessing,
  needsBiometricsStorage,
} from 'lib/verificationCheck';

import ExpiredDetectionConfigurationElement from './ExpiredDetectionConfigurationElement';

const DESCRIPTION_EDITABLE =
  'Configure which checks are run and which are required to pass in order for the verification to pass. ';
const DESCRIPTION_READONLY =
  'These are the checks required to pass in order for the verification to pass. Non-required checks will still run. ';

const CHECKS_FILTER_OPTIONS = [
  {
    label: 'Enabled',
    value: 'enabled',
  },
  {
    label: 'Disabled',
    value: 'disabled',
  },
  {
    label: 'Required',
    value: 'required',
  },
  {
    label: 'Non-required',
    value: 'non-required',
  },
  {
    label: 'Configurable',
    value: 'configurable',
  },
  {
    label: 'Non-configurable',
    value: 'non-configurable',
  },
];

const WarningBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: ${Spacing[7]};
  ${paragraphMixins.md};
  padding: 6px ${Spacing[3]};
  margin-top: -${Spacing[2]};
  margin-bottom: ${Spacing[2]};
  background-color: ${({ theme }) => theme.color.variants.caution.highlightLight};
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const WarningIcon = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${Spacing[3]};
`;

const DetailsLinkWrapper = styled.div`
  margin-left: auto;
  padding-left: ${Spacing[2]};
  flex: none;
  a {
    text-decoration: underline;
    color: ${({ theme }) => theme.color.text.primary};
  }
`;

const TitleContainer = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  grid-gap: ${Spacing[3]};
  width: 100%;
  align-items: center;
  margin-bottom: ${Spacing[3]};
`;

const RequiredWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-right: ${Spacing[7]};
`;

const TableTDWithSubRows = styled(TableTD).attrs(() => ({ ariaRole: 'row' }))`
  &[aria-expanded='true'] {
    border-bottom: none;
  }
`;

const TableIconTDWithSubRows = styled(TableIconTD).attrs(() => ({ ariaRole: 'row' }))`
  &[aria-expanded='true'] {
    border-bottom: none;
  }
`;

const AlignedCheckbox = styled(Checkbox)`
  justify-content: flex-end;
  padding-right: ${Spacing[5]};
`;

const LabelInfo = styled(Paragraph)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const StyledLabel = styled(Paragraph)`
  display: flex;
  gap: ${Spacing[2]};
`;

const AccordionSectionContainer = styled.div`
  border-radius: ${({ theme }) => theme.element.borderRadius};
  border: 1px solid ${({ theme }) => theme.color.border.secondary};
`;

type CheckModalType = 'barcode' | 'unrequire-warning';

const getCheckModalTypeToDisplay = ({
  updatedCheck,
  currentCheck,
  vtmplVersion,
}: {
  updatedCheck: VerificationCheckConfig;
  currentCheck: VerificationCheckConfig;
  vtmplVersion: VerificationTemplateVersion;
}): CheckModalType | undefined => {
  const isNowRequired =
    currentCheck['on-failed'] !== VerificationStatus.Failed &&
    updatedCheck['on-failed'] === VerificationStatus.Failed;
  const isNowNonRequired =
    currentCheck['on-failed'] === VerificationStatus.Failed &&
    updatedCheck['on-failed'] !== VerificationStatus.Failed;

  if (isBarcodeDependentCheck(updatedCheck) && isGovIdVtmplVersion(vtmplVersion) && isNowRequired) {
    const { 'enabled-id-classes-by-country': enabledIdClassesByCountry } = vtmplVersion.attributes;
    if (
      !hasBarcodeIdsEnabled(enabledIdClassesByCountry) ||
      getBarcodeIdsWithMissingCollection(enabledIdClassesByCountry).length > 0
    ) {
      return 'barcode';
    }
  } else if (isExplicitWarningCheck(updatedCheck) && isNowNonRequired) {
    return 'unrequire-warning';
  }
};

const highestTierAccess = (isEnhanced: boolean, isBasic: boolean): EditorAccessTier => {
  if (isEnhanced) {
    return EditorAccessTier.InquiriesEnhanced;
  } else if (isBasic) {
    return EditorAccessTier.InquiriesBasic;
  } else {
    return EditorAccessTier.InquiriesFree;
  }
};

interface FormVerificationTemplateChecksProps {
  vtmplVersion: VerificationTemplateVersion;
  matchRequirementProfiles: MatchRequirementProfile[];
  selectedMatchRequirement: MatchRequirementKeys | null;
  editable: boolean;
  isFormPristine: boolean;
  handleMatchRequirementSelected?: (matchRequirementKeys: MatchRequirementKeys) => void;
  handleUpdateVerificationTemplateVersion: (vtmplVersion: VerificationTemplateVersion) => void;
}

const FormVerificationTemplateChecks = ({
  vtmplVersion,
  matchRequirementProfiles,
  selectedMatchRequirement,
  isFormPristine,
  editable,
  handleMatchRequirementSelected,
  handleUpdateVerificationTemplateVersion,
}: FormVerificationTemplateChecksProps) => {
  // list of check configs persisted to the current vtmplv
  const existingChecks: VerificationCheckConfig[] = vtmplVersion.attributes['verification-checks'];

  const name = 'attributes';
  const videoConfigEnabled = useFeatureFlag('dashboard:ite-video-capture-config');

  const accountSelfieSimilarityEnabled = useProductFeature(
    ProductFeatureName.AccountsSelfieSimilarity,
  );

  const idCornerDetectionEnabled = useFeatureFlag('dashboard:corner-detection');

  const documentSyntheticContentDetectionEnabled =
    existingChecks.find(
      (existingCheck) => existingCheck.check === CheckName.DocumentSyntheticContentDetection,
    )?.['toggle-option'] === 'enabled';
  const documentTemplateDetectionEnabled = useFeatureFlag(
    'verifications:enable-document-template-detection',
  );
  const isDigitalTextModificationEnabled = useFeatureFlag(
    'verifications:enable-digital-text-modification-check',
  );

  const disabledGatedChecks = new Set<CheckName>();
  if (!videoConfigEnabled) {
    disabledGatedChecks.add(CheckName.IdVideoQualityDetection);
    disabledGatedChecks.add(CheckName.SelfieVideoQualityDetection);
  }

  if (!accountSelfieSimilarityEnabled) {
    disabledGatedChecks.add(CheckName.SelfieRepeatDetection);
  }

  if (!idCornerDetectionEnabled) {
    disabledGatedChecks.add(CheckName.IdCornerDetection);
  }

  if (!isDigitalTextModificationEnabled) {
    disabledGatedChecks.add(CheckName.DocumentDigitalTextModificationDetection);
  }

  if (!documentSyntheticContentDetectionEnabled) {
    disabledGatedChecks.add(CheckName.DocumentSyntheticContentDetection);
  }

  if (!documentTemplateDetectionEnabled) {
    disabledGatedChecks.add(CheckName.DocumentTemplateDetection);
  }

  if (!useFeatureFlag('verifications:enable-document-color-mode-detection')) {
    disabledGatedChecks.add(CheckName.DocumentColorModeDetection);
  }

  const allChecks: VerificationCheckConfig[] = useMemo(
    () =>
      getChecks(vtmplVersion.type)
        .map(
          (check) =>
            existingChecks.find((existingCheck) => existingCheck.check === check) ??
            stubDefaultCheck({
              check,
              required: false,
              enabled: false,
            }),
        )
        .filter(
          ({ check }) => !ITE_OMITTED_CHECKS.includes(check) && !disabledGatedChecks.has(check),
        )
        .sort((a, b) =>
          /**
           * sort by name rather than CheckName to ensure visual alpha-order
           * we maintain alpha-order so the order of default/non-default checks isn't reshuffled
           * when one is enabled and added to the redux form list
           */
          CHECK_DISPLAY_CONFIG[a.check].name.localeCompare(CHECK_DISPLAY_CONFIG[b.check].name),
        ),
    [disabledGatedChecks, existingChecks, vtmplVersion.type],
  );

  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const statusFilterLabelTextMapper = useLabelTextMapper(CHECKS_FILTER_OPTIONS, 'checks', {
    emptyIsAll: true,
  });

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSetSearchTerm] = useDebounced(setSearchTerm, 500, {
    leading: false,
    trailing: true,
  });

  const searchableChecks = useMemo<
    {
      check: CheckName;
      checkDisplayName: string;
      description: string;
      splitCheckDisplayName: string[];
    }[]
  >(
    () =>
      allChecks.map(({ check }) => {
        const checkDisplayNameSuffix = (() => {
          if (INTERNATIONAL_DATABASE_CHECK_TYPES.includes(check)) {
            return '(International Only)';
          } else if (US_ONLY_DATABASE_CHECK_TYPES.includes(check)) {
            return '(United States Only)';
          }
        })();

        const { name, description } = CHECK_DISPLAY_CONFIG[check];
        const checkDisplayName = checkDisplayNameSuffix
          ? `${name} ${checkDisplayNameSuffix}`
          : name;

        return {
          check,
          checkDisplayName,
          description,
          splitCheckDisplayName: checkDisplayName
            // TODO (walker): More comprehensively support all languages (Likely negate all unicode letters)
            .replace(/[!@#$%^&*()[\].-]/g, '')
            .split(' '),
        };
      }),
    [allChecks],
  );

  const fuse = useMemo(() => {
    const fuseOptions = {
      shouldSort: true,
      maxPatternLength: 28,
      useExtendedSearch: true,
      keys: [
        { name: 'checkDisplayName', weight: 0.4 },
        { name: 'description', weight: 0.3 },
        { name: 'splitCheckDisplayName', weight: 0.3 },
      ],
    };
    return new Fuse(searchableChecks, fuseOptions);
  }, [searchableChecks]);

  const hasUsedFilters = searchTerm !== '' || statusFilters.length > 0;
  const searchResults: Array<SearchResult> = useMemo(
    () =>
      searchTerm !== '' ? fuse.search(`^${searchTerm}`).map(({ item }) => item) : searchableChecks,
    [fuse, searchTerm, searchableChecks],
  );

  const handleExportVerificationChecksCsv = useCallback(async () => {
    const headers = ['Check name,Check slug,State,Description'];
    const checksCsvData = existingChecks
      .filter(({ check }) => !isDeprecatedCheck(check))
      .map(({ check, 'on-failed': onFailed, 'toggle-option': toggleOption }) => {
        let stateLabel = 'Disabled';
        if (toggleOption === 'enabled') {
          stateLabel = [VerificationStatus.Failed, VerificationStatus.RequiresRetry].includes(
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            onFailed,
          )
            ? 'Required'
            : 'Not required';
        }
        return [
          // checks are not always included in CHECK_DISPLAY_CONFIG, so we fallback to the check slug and empty description
          CHECK_DISPLAY_CONFIG[check]?.name ?? check,
          check,
          stateLabel,
          `"${CHECK_DISPLAY_CONFIG[check]?.description ?? ''}"`, // double quotes needed to escape commas in the description
        ].join(',');
      });
    const blob = new Blob([[...headers, ...checksCsvData].join('\n')], { type: 'text/csv' });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const displayName = getVtmplvDisplayName(vtmplVersion);
    link.download = `${vtmplVersion.id}_verification_checks (${displayName}).csv`;
    link.href = downloadUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [existingChecks, vtmplVersion]);

  return (
    <Card>
      <Card.Body>
        <Card.Title
          actions={
            <Tooltip
              side='top'
              trigger={
                <Button
                  width='auto'
                  variant='basic'
                  id='verfication-checks-table__export-button'
                  aria-label='Export'
                  prefixIcon={<TransferOutIcon />}
                  disabled={!isFormPristine}
                  onClick={handleExportVerificationChecksCsv}
                >
                  Export
                </Button>
              }
              disabled={isFormPristine}
            >
              Save your changes to export verification checks data.
            </Tooltip>
          }
        >
          Verification checks
          <HelpIcon icon={<CircleHelpIcon />}>
            Verification checks indicate something to note about the information that was submitted
            by an individual.
          </HelpIcon>
        </Card.Title>
        <SectionDescription>
          {editable ? DESCRIPTION_EDITABLE : DESCRIPTION_READONLY}
          <a
            target='_blank'
            rel='noreferrer'
            href='https://docs.withpersona.com/docs/verification-checks'
          >
            Learn more
          </a>
        </SectionDescription>
        <WarningBanner>
          <WarningIcon>
            <CircleInfoIcon />
          </WarningIcon>
          Requiring checks will impact the verification pass rate and overall conversion. Please
          make sure you understand and test any changes.
          <DetailsLinkWrapper>
            <Link target='_blank' rel='noreferrer' to='/dashboard/resources/verification-checks'>
              See details
            </Link>
          </DetailsLinkWrapper>
        </WarningBanner>
        <FormSection name={name} component={Fragment}>
          <TitleContainer>
            <SearchInput
              size='md'
              title={null}
              value={searchTerm}
              handleInput={debouncedSetSearchTerm}
              placeholder='Search for a check'
            />
            <MultiSelect
              size='md'
              data-test='verification-checks__multi-select'
              value={statusFilters}
              selectOptionValues={CHECKS_FILTER_OPTIONS}
              labelTextMapper={statusFilterLabelTextMapper}
              onChange={setStatusFilters}
            />
          </TitleContainer>
          <TableLayout
            showHeader
            aria-role='treegrid'
            dataTest='verification-checks-table'
            isEmpty={searchResults?.length === 0}
            containerMaxHeight={500}
            nullState={
              <TableNullState hasUsedFilters={hasUsedFilters}>
                No checks found on the verification template
              </TableNullState>
            }
            headerTR={
              <>
                <TableTH isSticky>
                  <Label size='lg' weight='bold'>
                    Checks
                  </Label>
                </TableTH>
                <TableTH isSticky />
                <TableTH isSticky>
                  <RequiredWrapper>
                    <Label size='md' weight='bold'>
                      Required
                    </Label>
                    <HelpIcon icon={<CircleHelpIcon />}>
                      If a required check fails, the associated verification attempt will fail.
                    </HelpIcon>
                  </RequiredWrapper>
                </TableTH>
              </>
            }
          >
            {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
            <FieldArray
              name='verification-checks'
              component={CheckTableRows}
              allChecks={allChecks}
              searchResults={searchResults}
              statusFilters={statusFilters}
              editable={editable}
              vtmplVersion={vtmplVersion}
              matchRequirementProfiles={matchRequirementProfiles}
              selectedMatchRequirement={selectedMatchRequirement}
              handleMatchRequirementSelected={handleMatchRequirementSelected}
              handleUpdateVerificationTemplateVersion={handleUpdateVerificationTemplateVersion}
            />
          </TableLayout>
        </FormSection>
      </Card.Body>
    </Card>
  );
};

interface SearchResult {
  check: CheckName;
  checkDisplayName: string;
}

interface CheckTableRowsProps {
  allChecks: VerificationCheckConfig[];
  searchResults: Array<SearchResult> | null;
  statusFilters: string[];
  editable: boolean;
  vtmplVersion: VerificationTemplateVersion;
  matchRequirementProfiles: MatchRequirementProfile[];
  selectedMatchRequirement: MatchRequirementKeys;
  handleMatchRequirementSelected: (matchRequirementKeys: MatchRequirementKeys) => void;
  handleUpdateVerificationTemplateVersion: (vtmplVersion: VerificationTemplateVersion) => void;
}

const CheckTableRows = ({
  allChecks,
  fields,
  searchResults,
  statusFilters,
  editable,
  vtmplVersion,
  matchRequirementProfiles,
  selectedMatchRequirement,
  handleMatchRequirementSelected,
  handleUpdateVerificationTemplateVersion,
}: CheckTableRowsProps & WrappedFieldArrayProps) => {
  // Enable all check configurations depends on user access tiers
  const hasAccessForTier = useHasAccessForTierDeterminator();
  const isEnhanced = hasAccessForTier(EditorAccessTier.InquiriesEnhanced);
  const isBasic = hasAccessForTier(EditorAccessTier.InquiriesBasic);
  const isITEInternal = useIsITEInternal();

  const existingChecks: VerificationCheckConfig[] = useMemo(() => fields.getAll() ?? [], [fields]);

  // Store pending check update in state for checks that we explicitly warn the user against un-requiring
  const [pendingUpdatedCheck, setPendingUpdatedCheck] = useState<VerificationCheckConfig | null>(
    null,
  );
  const [checkModalTypeToDisplay, setCheckModalTypeToDisplay] = useState<CheckModalType | null>(
    null,
  );

  const handleUpdateCheck = useCallback(
    (check: VerificationCheckConfig) => {
      const index = existingChecks.findIndex((c) => c.check === check.check);
      if (index === -1) {
        fields.push(check);
      } else {
        fields.splice(index, 1, check);
      }
    },
    [existingChecks, fields],
  );

  const handleUpdateCheckWithWarning = useCallback(
    (updatedCheck: VerificationCheckConfig) => {
      // find current check value to see if check is being un-required.
      const currentCheck = existingChecks.find((c) => c.check === updatedCheck.check) ?? {
        check: updatedCheck.check,
        'on-failed': VerificationStatus.Passed,
        'toggle-option': 'disabled',
      };
      // potentially surface a modal depending on check type and action.
      // - surface explicit warning modal for a set of critical checks when
      //   a user attempts to un-require them.
      // - surface warning modal when requiring barcode checks (detection, inconsistency, extraction inconsistency)
      //   if enabled ID types with barcodes are not configured to collect a barcode or back image.
      const checkModalType = getCheckModalTypeToDisplay({
        updatedCheck,
        currentCheck,
        vtmplVersion,
      });
      if (checkModalType != null) {
        setPendingUpdatedCheck(updatedCheck);
        setCheckModalTypeToDisplay(checkModalType);
      } else {
        handleUpdateCheck(updatedCheck);
      }
    },
    [existingChecks, vtmplVersion, handleUpdateCheck],
  );

  const {
    enabledRows,
    requiredRows,
    configurableRows,
    allRows,
  }: {
    enabledRows: ReactNode[];
    requiredRows: ReactNode[];
    configurableRows: ReactNode[];
    allRows: ReactNode[];
  } = useMemo(() => {
    const enabledRows: ReactNode[] = [];
    const requiredRows: ReactNode[] = [];
    const configurableRows: ReactNode[] = [];
    const allRows: ReactNode[] = [];

    searchResults?.forEach(({ check, checkDisplayName }: SearchResult) => {
      let configurationElement;
      const checkConfig = allChecks.find((check2) => check2.check === check);
      if (!checkConfig) {
        return;
      }

      const checkRequired =
        checkConfig['toggle-option'] === 'enabled' &&
        (checkConfig['on-failed'] === VerificationStatus.Failed ||
          checkConfig['on-failed'] === VerificationStatus.RequiresRetry);
      const checkDisabled = checkConfig['toggle-option'] === 'disabled';
      const checkGated = !checkEnabledByTier(
        checkConfig.check,
        highestTierAccess(isEnhanced, isBasic),
      );
      const configElementDisabled = checkDisabled || checkGated || !editable;

      if (isLegacyInternationalDatabaseCheck(existingChecks, checkConfig.check)) {
        return;
      }

      if (isAlphaCheck(checkConfig.check)) return;

      if (isMatchRequirementsCheck(checkConfig) && !checkGated) {
        configurationElement = (
          <MatchRequirementsConfigurationElement
            checkConfig={checkConfig}
            verificationTemplateVersionId={vtmplVersion.id}
            selectedMatchRequirement={selectedMatchRequirement}
            matchRequirementProfiles={matchRequirementProfiles}
            editable={!configElementDisabled}
            handleUpdateCheck={handleUpdateCheck}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            handleMatchRequirementSelected={handleMatchRequirementSelected}
          />
        );
      } else if (isExtractionCheck(checkConfig) && !checkGated) {
        configurationElement = (
          <ExtractedPropertiesConfigurationElement
            checkConfig={checkConfig}
            editable={!configElementDisabled}
            handleUpdateCheck={handleUpdateCheck}
          />
        );
      } else if (isSelfieAgeThresholdCheck(checkConfig) && isITEInternal && !checkGated) {
        configurationElement = (
          <SelfieAgeThresholdComparisonConfigurationElement
            checkConfig={checkConfig}
            editable={!configElementDisabled}
            handleUpdateCheck={handleUpdateCheck}
          />
        );
      } else if (isSelfieLivenessCheck(checkConfig) && isITEInternal && !checkGated) {
        configurationElement = (
          <SelfieLivenessConfigurationElement
            checkConfig={checkConfig}
            editable={!configElementDisabled}
            handleUpdateCheck={handleUpdateCheck}
          />
        );
      } else if (isRiskConfigCheck(checkConfig) && isITEInternal && !checkGated) {
        configurationElement = (
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          <RiskConfigConfigurationElement
            checkConfig={checkConfig}
            editable={!configElementDisabled}
            handleUpdateCheck={handleUpdateCheck}
          />
        );
      } else if (isDocumentExtractionCheck(checkConfig)) {
        const extractionFieldNames = uniq(
          (vtmplVersion as VerificationTemplateVersionDocument).attributes.extractions.map(
            (e) => e['field-name'],
          ),
        );
        configurationElement = (
          <DocumentExtractedPropertiesConfigurationElement
            checkConfig={checkConfig}
            editable={!configElementDisabled}
            extractionFieldNames={extractionFieldNames}
            handleUpdateCheck={handleUpdateCheck}
          />
        );
      } else if (isDocumentTypeCheck(checkConfig)) {
        // Function to set specific Verification Check and Extractions default based on a Document Type set
        const overrideVerificationChecksAndExtractionsByDocumentType = (
          documentType: DocumentType,
        ) => {
          if (vtmplVersion.type === ApiModel.VerificationTemplateVersionDocument) {
            const recommendedConfig = DOCUMENT_TYPE_TO_RECOMMENDED_CONFIG[documentType];
            if (recommendedConfig != null) {
              const updatedVerificationTemplateVersion = produce(
                vtmplVersion,
                (draftVerificationTemplateVersion) => {
                  draftVerificationTemplateVersion.attributes.extractions =
                    recommendedConfig['extractions'];
                  draftVerificationTemplateVersion.attributes['verification-checks'] =
                    recommendedConfig['verification-checks'];
                },
              );
              handleUpdateVerificationTemplateVersion(updatedVerificationTemplateVersion);
            }
          }
        };
        configurationElement = (
          <DocumentTypeConfigurationElement
            checkConfig={checkConfig}
            editable={!configElementDisabled}
            handleUpdateCheck={handleUpdateCheck}
            overrideVerificationChecksAndExtractionsByDocumentType={
              overrideVerificationChecksAndExtractionsByDocumentType
            }
          />
        );
      } else if (isBarcodeDetectionCheck(checkConfig)) {
        configurationElement = (
          <BarcodeDetectionConfigurationElement
            checkConfig={checkConfig}
            editable={!configElementDisabled}
            handleUpdateCheck={handleUpdateCheck}
          />
        );
      } else if (isDisallowedCountryCheck(checkConfig)) {
        configurationElement = (
          <DisallowedCountryConfigurationElement
            checkConfig={checkConfig}
            editable={!configElementDisabled}
            handleUpdateCheck={handleUpdateCheck}
          />
        );
      } else if (isExpirationCheck(checkConfig)) {
        configurationElement = (
          <ExpiredDetectionConfigurationElement
            checkConfig={checkConfig}
            editable={!configElementDisabled}
            handleUpdateCheck={handleUpdateCheck}
          />
        );
      } else if (isMrzDetectionCheck(checkConfig)) {
        configurationElement = (
          <MrzDetectionConfigurationElement
            checkConfig={checkConfig}
            editable={!configElementDisabled}
            handleUpdateCheck={handleUpdateCheck}
          />
        );
      } else if (checkConfig.check === CheckName.SelfieRepeatDetection) {
        const configurationElementStyleColor =
          !editable || checkDisabled ? 'disabled' : 'secondary';
        configurationElement = (
          <TableSubRow>
            <CheckFullSpanSubRow colSpan={3} isLast={true}>
              <LabelInfo color={configurationElementStyleColor} size='sm'>
                <WarningIcon>
                  <CircleInfoIcon />
                </WarningIcon>
                This check is only available as a non-required check. To enable, you must have
                account selfie similarity enabled to allow for selfie comparisons to other accounts.
              </LabelInfo>
            </CheckFullSpanSubRow>
          </TableSubRow>
        );
      } else if (isAgeLimitCheck(checkConfig)) {
        configurationElement = (
          <TableSubRow>
            <CheckFullSpanSubRow colSpan={3} isLast={true}>
              <AccordionSectionContainer>
                <AgeComparisonConfigurationElement
                  verificationCheck={checkConfig}
                  handleUpdateCheck={handleUpdateCheck}
                  editable={!configElementDisabled}
                />
              </AccordionSectionContainer>
            </CheckFullSpanSubRow>
          </TableSubRow>
        );
      } else if (isDocumentRecencyCheck(checkConfig)) {
        configurationElement = (
          <DocumentRecencyConfigurationElement
            checkConfig={checkConfig}
            editable={!configElementDisabled}
            handleUpdateCheck={handleUpdateCheck}
          />
        );
      }
      const checkRow = (
        <ChecksTableRow
          key={checkConfig.check}
          checkConfig={checkConfig}
          checkDisplayName={checkDisplayName}
          value={checkRequired}
          checkGated={checkGated}
          editable={editable}
          configurable={checkConfig['configurable'] !== false}
          configurationElement={configurationElement}
          accessTier={highestTierAccess(isEnhanced, isBasic)}
          handleUpdateCheck={handleUpdateCheckWithWarning}
        />
      );
      allRows.push(checkRow);
      if (!checkDisabled) {
        enabledRows.push(checkRow);
      }
      if (checkRequired) {
        requiredRows.push(checkRow);
      }
      if (configurationElement != null) {
        configurableRows.push(checkRow);
      }
    });
    return { enabledRows, requiredRows, configurableRows, allRows };
  }, [
    searchResults,
    allChecks,
    isEnhanced,
    isBasic,
    editable,
    isITEInternal,
    handleUpdateCheckWithWarning,
    vtmplVersion,
    selectedMatchRequirement,
    matchRequirementProfiles,
    handleUpdateCheck,
    handleMatchRequirementSelected,
    handleUpdateVerificationTemplateVersion,
  ]);

  const rows = useMemo<React.ReactNode[]>(() => {
    if (statusFilters.length === 0) {
      return allRows;
    } else {
      let rowsToShow: ReactNode[] = [];
      statusFilters.forEach((filter) => {
        switch (filter) {
          case 'enabled':
            rowsToShow = rowsToShow.concat(enabledRows);
            break;
          case 'disabled':
            rowsToShow = rowsToShow.concat(allRows.filter((row) => !enabledRows.includes(row)));
            break;
          case 'required':
            rowsToShow = rowsToShow.concat(requiredRows);
            break;
          case 'non-required':
            rowsToShow = rowsToShow.concat(allRows.filter((row) => !requiredRows.includes(row)));
            break;
          case 'configurable':
            rowsToShow = rowsToShow.concat(configurableRows);
            break;
          case 'non-configurable':
            rowsToShow = rowsToShow.concat(
              allRows.filter((row) => !configurableRows.includes(row)),
            );
            break;
        }
      });
      return uniq(rowsToShow);
    }
  }, [allRows, configurableRows, enabledRows, requiredRows, statusFilters]);

  const handleCheckWarningConfirm = useCallback(() => {
    setCheckModalTypeToDisplay(null);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    handleUpdateCheck(pendingUpdatedCheck);
    setPendingUpdatedCheck(null);
  }, [pendingUpdatedCheck, handleUpdateCheck]);

  const handleBarcodeCheckRequireConfirm = useCallback(
    (enabledIdClassesByCountry?: GovernmentIdEnabledIdClassByCountries) => {
      handleCheckWarningConfirm();
      if (
        enabledIdClassesByCountry != null &&
        vtmplVersion.type === ApiModel.VerificationTemplateVersionGovernmentId
      ) {
        const updatedVtmplVersion = produce(vtmplVersion, (draftVtmplVersion) => {
          draftVtmplVersion.attributes['enabled-id-classes-by-country'] = enabledIdClassesByCountry;
        });
        handleUpdateVerificationTemplateVersion(updatedVtmplVersion);
      }
    },
    [handleCheckWarningConfirm, vtmplVersion, handleUpdateVerificationTemplateVersion],
  );

  return (
    <>
      <CheckWarningModal
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        checkName={isExplicitWarningCheck(pendingUpdatedCheck) && pendingUpdatedCheck.check}
        isOpen={checkModalTypeToDisplay === 'unrequire-warning'}
        handleClose={() => setCheckModalTypeToDisplay(null)}
        handleConfirm={handleCheckWarningConfirm}
      />
      <BarcodeCheckModal
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        enabledIdClassesByCountry={
          isGovIdVtmplVersion(vtmplVersion) &&
          vtmplVersion.attributes['enabled-id-classes-by-country']
        }
        isOpen={checkModalTypeToDisplay === 'barcode'}
        handleClose={() => setCheckModalTypeToDisplay(null)}
        handleConfirm={handleBarcodeCheckRequireConfirm}
      />
      {rows}
    </>
  );
};

interface ChecksTableRowProps {
  value: boolean;
  checkConfig: VerificationCheckConfig;
  checkDisplayName: string;
  configurationElement?: ReactNode;
  checkGated: boolean;
  editable: boolean;
  configurable: boolean;
  accessTier: EditorAccessTier;
  handleUpdateCheck: (check: VerificationCheckConfig) => void;
}

export const INTERNATIONAL_DATABASE_CHECK_TYPES = [
  CheckName.DatabaseAddressComparison,
  CheckName.DatabaseBirthdateComparison,
  CheckName.DatabaseNameComparison,
  CheckName.DatabasePhoneNumberComparison,
  CheckName.DatabaseSocialSecurityNumberComparison,
  CheckName.DatabaseTwoPlusTwoDetection,
];

export const US_ONLY_DATABASE_CHECK_TYPES = [
  CheckName.DatabaseDeceasedDetection,
  CheckName.DatabasePoBoxDetection,
];

const ChecksTableRow = memo(function ChecksTableRow({
  checkConfig,
  checkDisplayName,
  value,
  configurationElement,
  checkGated,
  editable,
  configurable,
  handleUpdateCheck,
  accessTier,
}: ChecksTableRowProps) {
  const handleOnChecked = useCallback(
    (checked: boolean) =>
      handleUpdateCheck({
        ...checkConfig,
        'on-failed': checked ? VerificationStatus.Failed : null,
      }),
    [checkConfig, handleUpdateCheck],
  );

  const handleDisableCheck = useCallback(
    () =>
      handleUpdateCheck({
        ...checkConfig,
        'on-failed': null,
        'toggle-option': 'disabled',
      }),
    [checkConfig, handleUpdateCheck],
  );

  const handleEnableCheck = useCallback(
    () =>
      handleUpdateCheck({
        ...checkConfig,
        'toggle-option': 'enabled',
      }),
    [checkConfig, handleUpdateCheck],
  );

  // TODO(IFD-1004): refactor useToggle instead useState
  const [labelAccordionExpanded, setLabelAccordionExpanded] = useState<boolean>(false);
  // treegrid row is 'expanded' if nested element prop is provided
  const checkDisabled = checkConfig['toggle-option'] === 'disabled';
  const ariaExpanded = !checkDisabled && !!configurationElement && labelAccordionExpanded;
  const textStyleColor = !editable || checkDisabled ? 'disabled' : null;

  // check is not requireable due to existing compliance constraints
  const requireable = checkConfig.check !== CheckName.SelfieRepeatDetection;
  const idComparable = isIdCompareCheck(checkConfig);
  // This label can be used either on its own, or part of an accordion
  const label = (
    <>
      <StyledLabel color={textStyleColor} size='md' weight='bold'>
        {checkDisplayName}
        {needsBiometricsProcessing(checkConfig.check) && (
          <BiometricBadge
            dataMarketingId={`biometric-badge_${snakeCase(checkConfig.check)}-check-config`}
            stored={needsBiometricsStorage(checkConfig.check)}
          />
        )}
        {isBetaCheck(checkConfig.check) && <BetaLabel />}
        {isSunsetCheck(checkConfig.check) && <SunsetLabel />}
        {checkGated && (
          <ProductUpsellTooltip
            side='right'
            accessTier={accessTier}
            learnMoreId={`${checkConfig.check}-check-config`}
            trigger={
              <div>
                <InquiriesGatingSticker
                  dataMarketingIdSuffix={`${checkConfig.check}-check-config`}
                  stickerType={EnhancedGatingStickerType.Limited}
                />
              </div>
            }
          />
        )}
      </StyledLabel>
      <Paragraph color={textStyleColor} size='sm'>
        {CHECK_DISPLAY_CONFIG[checkConfig.check].description}
        {idComparable &&
          ' Configure which verification provides the comparison input in "ID verification comparison" section below.'}
      </Paragraph>
    </>
  );

  return (
    <>
      <TableTR data-test={`checks-table-row__${checkConfig.check}`}>
        <TableTDWithSubRows aria-expanded={ariaExpanded}>
          {configurationElement ? (
            <Accordion
              title={label}
              padded={false}
              overrideExpanded={checkDisabled ? false : null}
              disabled={checkDisabled}
              onChange={setLabelAccordionExpanded}
              defaultExpanded={false}
            />
          ) : (
            <CheckTitleContainer padded={false} titleContainerHeight='auto'>
              <div />
              <div>{label}</div>
            </CheckTitleContainer>
          )}
        </TableTDWithSubRows>
        <TableTDWithSubRows aria-expanded={ariaExpanded} />
        <TableIconTDWithSubRows aria-expanded={ariaExpanded}>
          <CheckRowControls
            id={checkConfig.check}
            requiredConfigControl={
              !checkDisabled ? (
                <AlignedCheckbox
                  data-test={`checks-table-row-checkbox__${checkConfig.check}`}
                  checked={value}
                  disabled={!editable || !requireable || !configurable || checkGated}
                  onChange={(e) => handleOnChecked(e.target.checked)}
                />
              ) : null
            }
            checkDisabled={checkDisabled}
            editable={editable}
            configurable={configurable}
            handleDisableCheck={handleDisableCheck}
            handleEnableCheck={handleEnableCheck}
          />
        </TableIconTDWithSubRows>
      </TableTR>
      {labelAccordionExpanded && !checkDisabled && configurationElement}
    </>
  );
});

const ControlsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: ${Spacing[4]};
`;

interface CheckRowControlsProps {
  id: string;
  requiredConfigControl: ReactNode;
  checkDisabled: boolean;
  editable: boolean;
  configurable: boolean;
  handleDisableCheck: () => void;
  handleEnableCheck: () => void;
}

const CheckRowControls = ({
  id,
  requiredConfigControl,
  checkDisabled,
  editable,
  configurable,
  handleDisableCheck,
  handleEnableCheck,
}: CheckRowControlsProps) => {
  const toggleAction = checkDisabled ? (
    <Button
      width='icon'
      variant='basic'
      id={`checks-table-row__${id}--enable`}
      data-test={`checks-table-row__${id}--enable`}
      onClick={handleEnableCheck}
      disabled={!editable || !configurable}
    >
      <PlusIcon />
    </Button>
  ) : (
    <ContextMenu
      data-test={`checks-table-row__${id}--actions-menu`}
      disabled={!editable || !configurable}
      items={[
        {
          id: 'disable',
          'data-test': `checks-table-row__${id}--disable`,
          onClick: handleDisableCheck,
          variant: 'danger' as const,
          label: 'Disable',
        },
      ]}
      renderItem={(item) => (
        <ContextMenu.Item
          key={item.id}
          id={`checks-table-row__${id}--action-menu-${item.id}`}
          onClick={(e) => {
            // Needed to avoid triggering row click below
            e.stopPropagation();
            e.preventDefault();
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            item.onClick();
          }}
          variant={item.variant}
        >
          {item.label}
        </ContextMenu.Item>
      )}
    />
  );
  return (
    <ControlsRow>
      {requiredConfigControl}
      <Spacer height={0} width={1.5} />
      {toggleAction}
    </ControlsRow>
  );
};

interface VerificationCheckConfigurationProps {
  title: string;
  helpText?: string | ReactNode;
  children: ReactNode;
  disabled: boolean;
}

const CheckFullSpanSubRow = styled(FullSpanSubRowTD)`
  padding-right: 64px;
`;

export const VerificationCheckConfiguration = ({
  title,
  helpText,
  children,
  disabled,
}: VerificationCheckConfigurationProps) => {
  return (
    <TableSubRow>
      <CheckFullSpanSubRow colSpan={3} isLast={true}>
        <AccordionSectionContainer>
          <Card>
            <Card.Body>
              <Card.Title style={{ margin: 0 }}>
                <Paragraph size='md' weight='bold' color={disabled ? 'disabled' : 'primary'}>
                  {title}
                </Paragraph>
                {helpText && <HelpIcon icon={<CircleHelpIcon />}>{helpText}</HelpIcon>}
              </Card.Title>
              {children}
            </Card.Body>
          </Card>
        </AccordionSectionContainer>
      </CheckFullSpanSubRow>
    </TableSubRow>
  );
};

export default FormVerificationTemplateChecks;
